import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { parseBuildToVersionCode } from "./version";

export type UpdateDecision =
  | { type: "none" }
  | { type: "recommend"; currentVersion: string; latestVersionCode: number }
  | { type: "force"; currentVersion: string; minVersionCode: number }
  | { type: "maintenance"; message: string };

export interface RemoteAppConfig {
  minVersionCodeAndroid: number;
  latestVersionCodeAndroid: number;
  minVersionCodeIos: number;
  latestVersionCodeIos: number;
  maintenanceMode: boolean;
  maintenanceMessage: string;
}

/**
 * 앱 부팅 시점에 원격 설정을 조회하고, 현재 앱 상태와 비교해 어떤 액션을 취할지 결정.
 *
 * 결정 우선순위: maintenance > force > recommend > none
 *
 * Fail-open: 설정 조회 실패 시 {type: "none"} 반환 → 오프라인/장애 시 앱이 갇히지 않음.
 */
export async function checkAppUpdate(): Promise<UpdateDecision> {
  if (!Capacitor.isNativePlatform()) return { type: "none" };

  let config: RemoteAppConfig;
  let info: Awaited<ReturnType<typeof App.getInfo>>;

  try {
    const [infoResult, configResult] = await Promise.all([
      App.getInfo(),
      fetchConfig(),
    ]);
    info = infoResult;
    if (!configResult) return { type: "none" };
    config = configResult;
  } catch (err) {
    console.error("[app-update] check failed, fail-open:", err);
    return { type: "none" };
  }

  // 1. Maintenance mode — 모든 사용자 차단
  if (config.maintenanceMode) {
    return {
      type: "maintenance",
      message: config.maintenanceMessage || "",
    };
  }

  const platform = Capacitor.getPlatform();
  const isAndroid = platform === "android";
  const isIOS = platform === "ios";
  if (!isAndroid && !isIOS) return { type: "none" };

  const minVersion = isAndroid ? config.minVersionCodeAndroid : config.minVersionCodeIos;
  const latestVersion = isAndroid ? config.latestVersionCodeAndroid : config.latestVersionCodeIos;
  const currentVersionCode = parseBuildToVersionCode(info.build);
  const currentVersionName = info.version ?? "";

  // 2. Force update — 이 버전 미만은 진입 차단
  if (minVersion > 0 && currentVersionCode < minVersion) {
    return {
      type: "force",
      currentVersion: currentVersionName,
      minVersionCode: minVersion,
    };
  }

  // 3. Recommend update — 비차단 배너
  if (latestVersion > 0 && currentVersionCode < latestVersion) {
    return {
      type: "recommend",
      currentVersion: currentVersionName,
      latestVersionCode: latestVersion,
    };
  }

  return { type: "none" };
}

async function fetchConfig(): Promise<RemoteAppConfig | null> {
  try {
    // AbortController로 2초 타임아웃 — 부팅 지연 방지
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const res = await fetch("/api/app/config", {
      signal: controller.signal,
      cache: "no-store",
    });
    clearTimeout(timeout);

    if (!res.ok) return null;
    return (await res.json()) as RemoteAppConfig;
  } catch {
    return null;
  }
}
